import React from 'react';
import { FiClock, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

interface InvoiceStatusBadgeProps {
  status: string;
  dueDate?: string;
  isOverdue?: boolean;
  size?: 'sm' | 'md' | 'lg';
  balanceDue?: number;
  totalAmount?: number;
}

const InvoiceStatusBadge: React.FC<InvoiceStatusBadgeProps> = ({
  status,
  dueDate,
  isOverdue = false,
  size = 'md',
  balanceDue,
  totalAmount
}) => {
  const base = 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium';

  const getStatusConfig = () => {
    switch (status.toLowerCase()) {
      case 'open':
        return {
          label: 'Open',
          icon: FiClock,
          className: `${base} bg-blue-50 text-blue-700 ring-1 ring-blue-200`
        };
      case 'overdue':
        return {
          label: 'Overdue',
          icon: FiAlertCircle,
          className: `${base} bg-red-50 text-red-700 ring-1 ring-red-200`
        };
      case 'partial_open':
        return {
          label: 'Partial Open',
          icon: FiClock,
          className: `${base} bg-amber-50 text-amber-800 ring-1 ring-amber-200`
        };
      case 'partial_over':
        return {
          label: 'Partial Overdue',
          icon: FiAlertCircle,
          className: `${base} bg-orange-50 text-orange-800 ring-1 ring-orange-200`
        };
      case 'closed':
        return {
          label: 'Closed',
          icon: FiCheckCircle,
          className: `${base} bg-green-50 text-green-700 ring-1 ring-green-200`
        };
      default:
        return {
          label: status,
          icon: FiClock,
          className: `${base} bg-gray-100 text-gray-700 ring-1 ring-gray-200`
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  const displayConfig = isOverdue ? {
    label: 'Overdue',
    icon: FiAlertCircle,
    className: `${base} bg-red-50 text-red-700 ring-1 ring-red-200`
  } : config;

  // Check if this is a partial payment
  const isPartialPayment = ['partial_open', 'partial_over'].includes(status.toLowerCase());
  const showPartialPaymentInfo = isPartialPayment && balanceDue !== undefined && totalAmount !== undefined;

  return (
    <div className="flex flex-col items-start">
      <span className={`
        ${displayConfig.className}
        ${sizeClasses[size]}
     `}>
        <Icon className={`mr-1.5 ${iconSizes[size]}`} />
        {displayConfig.label}
      </span>
      
      {/* Show partial payment information */}
      {showPartialPaymentInfo && (
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
          Partial Payment: ${balanceDue.toFixed(2)} due
        </div>
      )}
    </div>
  );
};

export default InvoiceStatusBadge; 